import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import {
  ArrowRight,
  Leaf,
  Recycle,
  TreeDeciduous,
  Droplets,
  Wind,
  Factory,
} from "lucide-react";

const IMAGES = {
  sustainability: "/sustainability-hero.jpg",
  nature: "https://images.unsplash.com/photo-1737276708671-256ad6c66240?crop=entropy&cs=srgb&fm=jpg&q=85",
};

const pillars = [
  {
    icon: Leaf,
    title: "Agricultural Waste Materials",
    description:
      "We source bagasse (sugarcane residue), areca palm leaves, and cornstarch — materials that would otherwise be burned or discarded.",
    impact: "100% waste-derived materials",
  },
  {
    icon: Recycle,
    title: "Compostable Lifecycle",
    description:
      "Our products biodegrade completely within 60-90 days under composting conditions, returning nutrients to the soil.",
    impact: "Zero persistent waste",
  },
  {
    icon: TreeDeciduous,
    title: "Reduced Carbon Footprint",
    description:
      "By replacing plastic and utilizing agricultural byproducts, we significantly reduce the carbon footprint of packaging.",
    impact: "70% less CO2 vs plastic",
  },
  {
    icon: Factory,
    title: "Responsible Supply Chain",
    description:
      "We work only with certified production partners who meet ethical labor and environmental standards.",
    impact: "Ethically produced",
  },
];

const SustainabilityPage = () => {
  return (
    <div data-testid="sustainability-page" className="noise-overlay">
      <Helmet>
        <title>Sustainability Commitment | Prithira Global Eco-Pledge</title>
        <meta name="description" content="Our sustainability commitment — 100% biodegradable raw materials, zero plastic, ethical sourcing, and a measurable plastic-replacement impact." />
        <meta name="keywords" content="sustainability biodegradable packaging, zero plastic supplier, ethical sourcing eco products, compostable supplier India" />
        <link rel="canonical" href="https://www.prithiraglobal.com/sustainability" />
        <meta property="og:title" content="Sustainability Commitment | Prithira Global" />
        <meta property="og:description" content="100% biodegradable, zero plastic, ethically sourced — our pledge to the planet." />
        <meta property="og:url" content="https://www.prithiraglobal.com/sustainability" />
      </Helmet>
      {/* Hero */}
      <section className="pt-32 pb-20 bg-sand-200/30">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center">
            <span className="caption text-primary mb-4 block">Our Purpose</span>
            <h1 className="heading-xl text-stone-900 mb-8">
              Sustainability <span className="italic">Commitment</span>
            </h1>
            <p className="body-lg text-stone-600 max-w-3xl mx-auto">
              Every product we create represents our commitment to environmental
              responsibility and a plastic-free future.
            </p>
          </div>
        </div>
      </section>

      {/* Hero Image */}
      <section className="bg-white pb-20">
        <div className="container-custom">
          <div className="relative rounded-2xl overflow-hidden">
            <img
              src={IMAGES.sustainability}
              alt="Reduce, Reuse, Recycle — Prithira Global sustainability commitment"
              className="w-full aspect-[21/9] object-cover"
            />
          </div>
        </div>
      </section>

      {/* Our Mission */}
      <section className="section-spacing bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="caption text-primary mb-4 block">Our Mission</span>
              <h2 className="heading-lg text-stone-900 mb-6">
                Replacing <span className="italic">Plastic</span>, One Meal at a Time
              </h2>
              <div className="space-y-6 text-stone-600 font-light leading-relaxed">
                <p>
                  The world produces over 400 million tonnes of plastic waste
                  annually. Single-use food packaging contributes significantly
                  to this crisis, with most ending up in landfills or our oceans.
                </p>
                <p>
                  At Prithira Global, we believe the solution lies in nature.
                  By transforming agricultural byproducts into functional,
                  beautiful tableware, we're proving that sustainability and
                  quality can coexist.
                </p>
                <p>
                  Our mission is simple: make sustainable packaging accessible
                  to businesses worldwide, so every meal can be served without
                  harming our planet.
                </p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { value: "400M", label: "Tonnes plastic/year globally" },
                { value: "8M", label: "Tonnes in oceans annually" },
                { value: "90", label: "Days to biodegrade our products" },
                { value: "0", label: "Plastic in our materials" },
              ].map((stat, index) => (
                <div
                  key={index}
                  className="bg-sand-200/50 rounded-xl p-6 text-center"
                >
                  <span className="text-3xl font-heading text-primary block mb-1">
                    {stat.value}
                  </span>
                  <span className="text-xs text-stone-600">{stat.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Pillars */}
      <section className="section-spacing bg-sand-200/30">
        <div className="container-custom">
          <div className="text-center mb-16">
            <span className="caption text-primary mb-4 block">Our Approach</span>
            <h2 className="heading-lg text-stone-900 mb-6">
              Four Pillars of <span className="italic">Sustainability</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {pillars.map((pillar, index) => (
              <div
                key={index}
                data-testid={`pillar-${index}`}
                className="bg-white rounded-2xl p-8 border border-stone-100"
              >
                <div className="flex items-start gap-6">
                  <div className="w-16 h-16 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                    <pillar.icon className="h-8 w-8 text-primary" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h3 className="font-heading text-xl text-stone-900 mb-3">
                      {pillar.title}
                    </h3>
                    <p className="text-stone-600 font-light text-sm leading-relaxed mb-4">
                      {pillar.description}
                    </p>
                    <span className="inline-flex items-center bg-primary/10 text-primary px-4 py-2 rounded-full text-xs font-medium">
                      {pillar.impact}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Material Journey */}
      <section className="section-spacing bg-white">
        <div className="container-custom">
          <div className="text-center mb-16">
            <span className="caption text-primary mb-4 block">The Journey</span>
            <h2 className="heading-lg text-stone-900 mb-6">
              From <span className="italic">Farm</span> to Table to Earth
            </h2>
          </div>
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-5 gap-4">
              {[
                { icon: Leaf, label: "Harvest", desc: "Agricultural residue collected" },
                { icon: Factory, label: "Process", desc: "Cleaned and prepared" },
                { icon: Wind, label: "Mold", desc: "Shaped into products" },
                { icon: Droplets, label: "Use", desc: "Serves its purpose" },
                { icon: Recycle, label: "Return", desc: "Biodegrades naturally" },
              ].map((step, index) => (
                <div key={index} className="text-center">
                  <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3">
                    <step.icon className="h-6 w-6 text-primary" strokeWidth={1.5} />
                  </div>
                  <h4 className="font-heading text-sm text-stone-900 mb-1">
                    {step.label}
                  </h4>
                  <p className="text-xs text-stone-500">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-spacing bg-sand-200/30">
        <div className="container-custom text-center">
          <h2 className="heading-lg text-stone-900 mb-6">
            Join the <span className="italic">Movement</span>
          </h2>
          <p className="body-base text-stone-600 mb-10 max-w-2xl mx-auto">
            Partner with Prithira Global and make sustainability a competitive
            advantage for your business.
          </p>
          <Link to="/partner">
            <Button
              data-testid="sustainability-cta-btn"
              className="rounded-full px-8 py-6 text-base font-medium bg-primary text-primary-foreground hover:bg-primary/90 btn-lift"
            >
              Partner With Us
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default SustainabilityPage;
